import {
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ChangeEvent } from "react";
import {
  DEFAULT_DRAG_HANDLE_SELECTOR,
  NO_DRAG_SELECTOR,
} from "../../../app/constants";
import { useAppSelector } from "../../../app/hooks";
import CustomAutocompleteMulti from "../../../common/components/functional/customAutocomplete/CustomAutocompleteMulti";
import { SpinnerButton } from "../../../common/components/functional/spinnerButton/SpinnerButton";
import { TextFieldLight } from "../../../common/components/styled/styled";
import { AutocompleteType } from "../../../common/types/autoComplete";
import theme from "../../../theme";
import { EezyCompany } from "../../invoices/invoiceAPI";
import { expertNonAdminRoles } from "../../user/accessRightChecks";
import { ExpertUser } from "../../user/types/entities";

type Props = {
  onSubmit: () => void;
  eezyCompanyOptions: EezyCompany[];
  expert: ExpertUser | undefined;
};

export const ExpertForm = ({ onSubmit, eezyCompanyOptions, expert }: Props) => {
  const { control, formState, watch, setValue, trigger } = useFormContext();
  const { t } = useTranslation();

  const { status } = useAppSelector((state) => state.expertUsers);
  const isLoading = status === "pending";
  const scopeOptions: AutocompleteType[] = expertNonAdminRoles.map((r) => ({
    id: r.id,
    name: t(`settings:${r.name}`),
  }));

  const isAdmin: boolean = watch("isAdmin");
  const isExpertTypeGroup: boolean = watch("expertType") === "group";
  const filteredEezyCompanyOptions = eezyCompanyOptions.filter((option) => {
    if (isExpertTypeGroup) {
      return !option.isFranchise;
    } else {
      return option.isFranchise;
    }
  });

  return (
    <Box className={NO_DRAG_SELECTOR}>
      <Box m={1}>
        <Typography>{t("firstName")}</Typography>
        <Controller
          control={control}
          name="firstName"
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <FormControl fullWidth>
              <TextFieldLight
                value={value}
                disabled={!!expert}
                size="small"
                onChange={(value: ChangeEvent<HTMLInputElement>) =>
                  onChange(value)
                }
              />
            </FormControl>
          )}
        />
      </Box>
      <Box m={1}>
        <Typography>{t("lastName")}</Typography>
        <Controller
          control={control}
          name="lastName"
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <FormControl fullWidth>
              <TextFieldLight
                value={value}
                disabled={!!expert}
                size="small"
                onChange={(value: ChangeEvent<HTMLInputElement>) =>
                  onChange(value)
                }
              />
            </FormControl>
          )}
        />
      </Box>
      <Box m={1}>
        <Typography>{t("settings:company")}</Typography>
        <Controller
          control={control}
          name="expertType"
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <FormControl fullWidth>
              <RadioGroup
                row
                name="expertType"
                value={value}
                onChange={(ev) => {
                  onChange(ev.target.value);
                  setValue("eezyCompanies", []);
                }}
              >
                <FormControlLabel
                  value={"group"}
                  control={<Radio />}
                  label={t("settings:group")}
                />
                <FormControlLabel
                  value={"franchise"}
                  control={<Radio />}
                  label={t("settings:franchise")}
                />
              </RadioGroup>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="eezyCompanies"
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <FormControl fullWidth>
              <Autocomplete
                multiple
                value={value}
                size="small"
                getOptionLabel={(option) => option.name}
                onChange={(_: any, newValue: EezyCompany[]) =>
                  onChange(newValue)
                }
                options={filteredEezyCompanyOptions}
                renderInput={(params: any) => (
                  <TextFieldLight {...params} disabled={!!expert} />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.eezyCompanyId === value.eezyCompanyId
                }
                getOptionDisabled={() =>
                  isExpertTypeGroup ? value.length >= 1 : value.length >= 3
                }
              />
            </FormControl>
          )}
        />
      </Box>
      <Box m={1}>
        <Typography>{t("email")}</Typography>
        <Controller
          control={control}
          name="emailAddress"
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <FormControl fullWidth>
              <TextFieldLight
                value={value}
                disabled={!!expert}
                size="small"
                onChange={(value: ChangeEvent<HTMLInputElement>) =>
                  onChange(value)
                }
              />
            </FormControl>
          )}
        />
      </Box>
      <Box m={1}>
        <Typography>{t("settings:role")}</Typography>
        <Controller
          control={control}
          name="isAdmin"
          render={({ field: { value, onChange } }) => (
            <RadioGroup
              row
              name="isAdmin"
              value={value}
              onChange={() => {
                const newIsAdmin = !isAdmin;
                onChange(newIsAdmin);
                if (newIsAdmin) {
                  setValue("authScopes", [{ id: "a", name: "" }]);
                } else {
                  setValue("authScopes", []);
                }
                trigger("authScopes");
              }}
            >
              <FormControlLabel
                value={true}
                control={<Radio />}
                label={t("settings:admin")}
              />
              <FormControlLabel
                value={false}
                control={<Radio />}
                label={t("settings:other")}
              />
            </RadioGroup>
          )}
        />

        <Controller
          control={control}
          name="authScopes"
          render={({ field: { value, onChange } }) =>
            !isAdmin ? (
              <FormControl fullWidth>
                <CustomAutocompleteMulti
                  options={scopeOptions}
                  defaultValue={value}
                  fullWidth
                  name="authScopes"
                  onChange={(_: any, newValue: AutocompleteType[]) =>
                    onChange(newValue)
                  }
                />
              </FormControl>
            ) : (
              <></>
            )
          }
        />
      </Box>
      <Box
        className={DEFAULT_DRAG_HANDLE_SELECTOR}
        m={1}
        sx={{ cursor: "grab" }}
      >
        <SpinnerButton
          label={t("save")}
          onClick={onSubmit}
          disabled={!formState.isDirty || !formState.isValid}
          variant="contained"
          isLoading={isLoading}
          color="primary"
          sx={{ height: "36px", borderRadius: "20px" }}
          spinnerSx={{ color: theme.palette.primary.main }}
        />
      </Box>
    </Box>
  );
};
